import React from 'react';
import { motion } from 'framer-motion';
import { ServiceCard, Service } from './ServiceCard';
import { IconWrapper } from '../common/IconWrapper';
import {
  HiCpuChip,
  HiCloud,
  HiCodeBracket,
  HiServer,
  HiChartBar,
  HiArrowPath
} from 'react-icons/hi2';
import { HiDatabase } from 'react-icons/hi';
import { HiDevicePhoneMobile } from 'react-icons/hi2';

// Service categories with their services
const serviceCategories = [
  {
    category: 'Agentic AI',
    icon: HiCpuChip,
    services: [
      {
        id: 'azure-foundry',
        title: 'Azure Foundry',
        description: 'Build and deploy AI applications at scale with Azure AI Foundry platform.',
        icon: HiCpuChip,
        features: ['Azure AI Foundry', 'Model Development', 'AI Deployment', 'Responsible AI'],
        link: '/services/azure-foundry'
      },
      {
        id: 'azure-open-ai',
        title: 'Azure Open AI',
        description: 'Leverage Azure OpenAI services including GPT-4, GPT-3.5, and embedding models.',
        icon: HiCpuChip,
        features: ['Azure OpenAI', 'GPT-4 Integration', 'Custom Fine-tuning', 'Prompt Engineering'],
        link: '/services/azure-open-ai'
      }
    ]
  },
  {
    category: 'Data Engineering & Analytics',
    icon: HiDatabase,
    services: [
      {
        id: 'data-warehouse',
        title: 'Datalake/Lakeview/Data Warehouse',
        description: 'Build scalable data warehouses and data lakes using SQL, Snowflake, and ADLS.',
        icon: HiDatabase,
        features: ['Data Lakes', 'SQL Warehouses', 'Snowflake', 'ADLS'],
        link: '/services/data-warehouse'
      },
      {
        id: 'etl',
        title: 'ETL (Azure Data Factory/Databricks)',
        description: 'Build robust ETL pipelines using Azure Data Factory and Databricks.',
        icon: HiArrowPath,
        features: ['Azure Data Factory', 'Databricks', 'Data Transformation', 'Pipeline Monitoring'],
        link: '/services/etl'
      },
      {
        id: 'bi-reporting',
        title: 'BI Reporting (Power BI)',
        description: 'Create powerful business intelligence solutions with Power BI dashboards and reports.',
        icon: HiChartBar,
        features: ['Power BI', 'Executive Dashboards', 'Self-Service Analytics', 'Data Modeling'],
        link: '/services/bi-reporting'
      }
    ]
  },
  {
    category: 'Application Engineering',
    icon: HiCodeBracket,
    services: [
      {
        id: 'application-development',
        title: 'Application Development',
        description: 'Build modern, scalable applications with cutting-edge technologies for web and mobile.',
        icon: HiCodeBracket,
        features: ['Full-stack Development', 'Web Applications', 'Mobile Apps', 'API Development'],
        link: '/services/application-development'
      },
      {
        id: 'application-modernization',
        title: 'Application Modernization',
        description: 'Transform legacy applications into modern, cloud-native systems.',
        icon: HiArrowPath,
        features: ['Legacy Migration', 'Microservices', 'Cloud Migration', 'Technology Upgrade'],
        link: '/services/application-modernization'
      }
    ]
  },
  {
    category: 'Cloud Data Services',
    icon: HiCloud,
    services: [
      {
        id: 'cloud-azure',
        title: 'Azure',
        description: 'Comprehensive Azure cloud services for infrastructure, data, AI, and applications.',
        icon: HiCloud,
        features: ['Azure Infrastructure', 'Azure Data Services', 'Azure AI', 'Azure DevOps'],
        link: '/services/cloud-azure'
      },
      {
        id: 'cloud-gcp',
        title: 'GCP',
        description: 'Google Cloud Platform services for infrastructure, analytics, AI/ML, and applications.',
        icon: HiCloud,
        features: ['GCP Infrastructure', 'BigQuery', 'Vertex AI', 'Cloud Build'],
        link: '/services/cloud-gcp'
      },
      {
        id: 'cloud-aws',
        title: 'AWS',
        description: 'Amazon Web Services for infrastructure, data, AI, and application deployment.',
        icon: HiCloud,
        features: ['AWS Infrastructure', 'AWS Data Services', 'SageMaker', 'AWS DevOps'],
        link: '/services/cloud-aws'
      }
    ]
  },
  {
    category: 'DevOps',
    icon: HiServer,
    services: [
      {
        id: 'cicd-pipelines',
        title: 'CI/CD Pipelines',
        description: 'Build robust continuous integration and deployment pipelines for faster, reliable releases.',
        icon: HiServer,
        features: ['CI/CD Setup', 'Infrastructure as Code', 'Container Orchestration', 'Monitoring & Logging'],
        link: '/services/cicd-pipelines'
      }
    ]
  }
];

// Flatten services for grid display
const services: Service[] = serviceCategories.flatMap(category => 
  category.services.map(service => ({
    ...service,
    icon: service.icon
  }))
);

export const ServicesGrid: React.FC = () => {
  return (
    <section className="section">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Our Services</h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Comprehensive technology solutions organized by category. From AI and data engineering to cloud services and DevOps.
          </p>
        </motion.div>

        {/* Display services by category */}
        {serviceCategories.map((category, categoryIndex) => (
          <motion.div
            key={category.category}
            className="mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: categoryIndex * 0.1 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <IconWrapper icon={category.icon} className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white">{category.category}</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {category.services.map((service, index) => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  index={index}
                />
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};
